import '@/scripts/product-model'
import EmblaCarousel from 'embla-carousel'
import { addNextPrevBtn } from '@/scripts/prevAndNextButtons'
import { addDotsBtn } from '@/scripts/dotButtons'
import { onThumbClick, followMainCarousel } from '@/scripts/thumbButtons'

class ProductGallery extends HTMLElement {
  constructor () {
    super()

    this.isZoomEnable = false
    this.zoomEl = document.querySelectorAll('.js-zoom-image')
    this.zoomImageEl = document.querySelectorAll('.js-zoom-image img')

    this.setup = this.setup.bind(this)
    this.zoomImg = this.zoomImg.bind(this)
    this.bodyScroll = this.bodyScroll.bind(this)
    this.magic360 = this.magic360.bind(this)
    this.setup()
    this.bodyScroll()
    this.magic360()

    if (window.$) this.handleJqueryInit()
    window.document.addEventListener('initJquery', this.handleJqueryInit.bind(this))
  }

  handleJqueryInit () {
    import('jquery-zoom').then(() => {
      if (!this.isMobile() && !this.isZoomEnable) this.zoomImg()
      if (this.isMobile()) this.destroyZoomImg()
      this.zoomImg()
    })
  }

  handleEmblaInit (carousel) {
    return EmblaCarousel(carousel, {
      selectedClass: "",
      containScroll: "trimSnaps",
      dragFree: true,
      axis: 'x',
      slidesToScroll: 1,
      breakpoints: {
        '(min-width: 768px)': {
          axis: 'y',
        }
      }
    }, [])
  }

  isMobile () {
    return window.innerWidth < 768
  }

  zoomImg () {
    if (this.zoomEl && !this.isMobile()) {
      this.zoomEl.forEach(item => jQuery(item).zoom({magnify: 1.5}))
      this.isZoomEnable = true
    }

    if (this.zoomImageEl && this.isMobile()) {
      this.zoomImageEl.forEach(item => {
        this.pinchZoom(item)
      })
    }
  }

  destroyZoomImg () {
    if (this.zoomEl) {
      this.zoomEl.forEach(item => jQuery(item).trigger('zoom.destroy'))
      this.isZoomEnable = false
    }
  }

  setup () {
    const mainCarouselView = this.querySelector('.js-main-carousel')
    const thumbCarouselView = this.querySelector('.js-thumbnails-carousel')
    if (mainCarouselView && thumbCarouselView) {
      const mainCarouselOptions = {
        selectedClass: "",
        loop: false,
        draggable: false,
        skipSnaps: false,
        breakpoints: {
          '(max-width: 767px)': {
            draggable: true
          }
        }
      }
      const mainCarousel = EmblaCarousel(mainCarouselView, mainCarouselOptions , [])

      const thumbCarousel = this.handleEmblaInit(thumbCarouselView)

      thumbCarousel.slideNodes().forEach((thumbNode, index) => {
        const onClick = onThumbClick(mainCarousel, thumbCarousel, index)
        thumbNode.addEventListener("click", onClick, false)
        thumbNode.addEventListener('keyup', (event) => {
          const eventCode = event.code.toUpperCase()
          thumbNode.setAttribute('aria-pressed', 'false')
          if (eventCode === "ENTER" || eventCode === "SPACE") {
            onClick()
            thumbNode.setAttribute('aria-pressed', 'true')
          }
        })
      })

      const syncThumbCarousel = followMainCarousel(mainCarousel, thumbCarousel)
      mainCarousel.on("select", () => {
        syncThumbCarousel()
        const epigraphAR = this.querySelector('epigraph-ar')
        if (epigraphAR) {
          const newMainCarouselOptions = {
            ...mainCarouselOptions,
            breakpoints: {
              '(max-width: 767px)': {
                draggable: false
              }
            }
          }
          const activeIndex = mainCarousel.selectedScrollSnap()
          if (activeIndex && activeIndex === 1) {
            mainCarousel.reInit(newMainCarouselOptions)
          } else {
            mainCarousel.reInit(mainCarouselOptions)
          }
        }
      })
      thumbCarousel.on("init", syncThumbCarousel)
      // Add next prev buttons for Thumbnails carousel
      const prevBtn = this.querySelector('.js-embla-prev')
      const nextBtn = this.querySelector('.js-embla-next')
      const dotsThumbnail = this.querySelector(".js-embla-thumbnail-dots")
      addNextPrevBtn(prevBtn, nextBtn, thumbCarousel)
      addDotsBtn(dotsThumbnail, thumbCarousel)
    }
  }

  magic360 () {
    const targetNode = document.body
    const config = { childList: true, subtree: true }

    const callback = (mutationList, observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          const m360ButtonEl = this.querySelector('button.m360-icon-fullscreen-open')
          if (m360ButtonEl) {
            m360ButtonEl.setAttribute('aria-label', 'Open 360 fullscreen')
          }

          const canvasEl = this.querySelector('.js-no-transition canvas')
          if (canvasEl) {
            canvasEl.setAttribute('aria-hidden', true)
          }
        }
      }
    }
    const observer = new MutationObserver(callback)
    observer.observe(targetNode, config)
  }

  pinchZoom (imageElement) {
    let imageElementScale = 1

    let start = {}

    // Calculate distance between two fingers
    const distance = (event) => {
      return Math.hypot(event.touches[0].pageX - event.touches[1].pageX, event.touches[0].pageY - event.touches[1].pageY)
    }

    imageElement.addEventListener('touchstart', (event) => {
      if (event.touches.length === 2) {
        event.preventDefault() // Prevent page scroll

        // Calculate where the fingers have started on the X and Y axis
        start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2
        start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2
        start.distance = distance(event)
      }
    })

    imageElement.addEventListener('touchmove', (event) => {
      if (event.touches.length === 2) {
        event.preventDefault() // Prevent page scroll

        // Safari provides event.scale as two fingers move on the screen
        // For other browsers just calculate the scale manually
        let scale
        if (event.scale) {
          scale = event.scale
        } else {
          const deltaDistance = distance(event)
          scale = deltaDistance / start.distance
        }
        imageElementScale = Math.min(Math.max(1, scale), 4)

        // Calculate how much the fingers have moved on the X and Y axis
        const deltaX = (((event.touches[0].pageX + event.touches[1].pageX) / 2) - start.x) * 2 // x2 for accelarated movement
        const deltaY = (((event.touches[0].pageY + event.touches[1].pageY) / 2) - start.y) * 2 // x2 for accelarated movement

        // Transform the image to make it grow and move with fingers
        const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${imageElementScale})`
        imageElement.style.transform = transform
        imageElement.style.WebkitTransform = transform
        imageElement.style.zIndex = '9999'
      }
    })

    imageElement.addEventListener('touchend', (event) => {
      // Reset image to it's original format
      imageElement.style.transform = ''
      imageElement.style.WebkitTransform = ''
      imageElement.style.zIndex = ''
    })
  }

  bodyScroll () {
    const epigraphAR = document.querySelector('epigraph-ar')
    if (epigraphAR) {
      epigraphAR.addEventListener('epigraph-valid-product', () => {
        setTimeout(() => {
          const qrModal = epigraphAR.shadowRoot.querySelector('qr-modal')
          const modal = qrModal ? qrModal.shadowRoot.querySelector('.modal') : null
          // disable scroll when open modal
          if (modal) {
            const config = { attributes: true }
            const callback = (mutationList, observer) => {
              for (const mutation of mutationList) {
                if (mutation.type === 'attributes') {
                  if (modal.classList.contains('show')) {
                    document.body.style.overflow = 'hidden'
                  } else {
                    document.body.style.overflow = 'visible'
                  }
                }
              }
            }
            const observer = new MutationObserver(callback)
            observer.observe(modal, config)
          }
        }, 500)
      })
    }
  }
}

customElements.define('product-gallery', ProductGallery)
